import {
  Button,
  Callout,
  Code,
  H3,
  Intent,
  Tag,
  Tooltip,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { isBrowserAuthConnection } from 'vet-bones/utils';

import { CopyToClipboardButton } from 'src/ui/components/CopyToClipboardButton';
import {
  LAUNCHPAD_CONNECTION_EXPANDED_INFO,
  LAUNCHPAD_CONNECTION_SSO_BADGE,
  LAUNCHPAD_CONNECTION_SSO_DELETE_BUTTON,
  LAUNCHPAD_CONNECTION_SSO_PROVIDER_WARNING,
  LAUNCHPAD_COPY_ENDPOINT_BUTTON,
  LAUNCHPAD_COPY_TOKEN_BUTTON,
} from 'src/ui/constants/testIds';
import { Connection as GraphConnection } from 'src/ui/graph/types';
import { components } from 'src/ui/templates/copy';
import { isCopyConnectionTokenButtonEnabled } from 'src/ui/utils/features';

type ConnectionDetailsProps = {
  connection: GraphConnection;
  onReauthenticateSSOConnection: () => void;
  onReauthenticateUserPasswordConnection: () => void;
  onDeleteConnection: () => void;
  isBrowserAuthFailure: boolean;
  isUnauthorized: boolean;
};

/* istanbul ignore next */
export const ConnectionDetails: React.FC<ConnectionDetailsProps> = ({
  connection,
  onReauthenticateSSOConnection,
  onReauthenticateUserPasswordConnection,
  onDeleteConnection,
  isUnauthorized,
  isBrowserAuthFailure,
}) => {
  const { connectionDetails } = components.launchpad;

  const renderReauthorizationButton = (isSSO: boolean) => (
    <Button
      icon={IconNames.Refresh}
      intent={Intent.WARNING}
      minimal
      outlined
      text={components.launchpad.connectionDetails.buttons.reauthenticate}
      onClick={
        isSSO
          ? onReauthenticateSSOConnection
          : onReauthenticateUserPasswordConnection
      }
    />
  );

  const renderAuthorizationError = () => {
    const renderCallout = (title: string, content: JSX.Element | string) => (
      <Callout
        className="launchpad-dashboard__connection-details__error-callout"
        title={title}
        intent={Intent.WARNING}
      >
        <div>{content}</div>
      </Callout>
    );

    const handleBrowserAuthFailure = () =>
      renderCallout(
        connectionDetails.errorCallouts.browserAuthFailure.title,
        connectionDetails.errorCallouts.browserAuthFailure.body
      );

    const handleSSOFailure = () => {
      if (!connection.token) {
        return renderCallout(
          connectionDetails.errorCallouts.ssoAuthFailure.noToken.title,
          connectionDetails.errorCallouts.ssoAuthFailure.noToken.body(
            renderReauthorizationButton(true)
          )
        );
      }

      return renderCallout(
        connectionDetails.errorCallouts.ssoAuthFailure.existingToken.title,
        connectionDetails.errorCallouts.ssoAuthFailure.existingToken.body(
          renderReauthorizationButton(true)
        )
      );
    };

    const handleGenericAuthFailure = () => {
      return renderCallout(
        connectionDetails.errorCallouts.basicAuthFailure.title,
        connectionDetails.errorCallouts.basicAuthFailure.body(
          renderReauthorizationButton(false)
        )
      );
    };

    if (connection.ssoProvider && !connection.ssoProvider.isValid) {
      return (
        <Callout
          className="launchpad-dashboard__connection-details__error-callout"
          title="SSO Configuration Mismatch"
          intent={Intent.WARNING}
          data-testid={LAUNCHPAD_CONNECTION_SSO_PROVIDER_WARNING}
        >
          <div>
            {`This endpoint was configured with an SSO provider (Client ID: ${connection.ssoProvider.clientId}) ` +
              `that no longer exists. This can happen when SSO settings are changed by an administrator. ` +
              `Delete this endpoint and create a new one.`}
          </div>
          <Button
            className="launchpad-dashboard__connection-details__delete-button"
            icon={IconNames.Trash}
            intent={Intent.DANGER}
            minimal
            outlined
            text="Delete Endpoint"
            onClick={onDeleteConnection}
            data-testid={LAUNCHPAD_CONNECTION_SSO_DELETE_BUTTON}
          />
        </Callout>
      );
    }

    if (connection.useConnectionSSO && isUnauthorized) {
      return handleSSOFailure();
    }

    if (isBrowserAuthConnection(connection) && isBrowserAuthFailure) {
      return handleBrowserAuthFailure();
    }

    if (
      !connection.useConnectionSSO &&
      !isBrowserAuthConnection(connection) &&
      isUnauthorized
    ) {
      return handleGenericAuthFailure();
    }

    return null;
  };

  const renderExpandedInfo = () => {
    const { ssoProvider } = connection;
    const hasValidSsoProvider = ssoProvider?.isValid && ssoProvider?.clientId;
    const hasInternalEndpoint = !!connection.internalEndpoint;

    if (!hasValidSsoProvider && !hasInternalEndpoint) {
      return null;
    }

    const formatProviderType = (type: string): string => {
      return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    };

    return (
      <details
        className="launchpad-dashboard__connection-details__advanced"
        data-testid={LAUNCHPAD_CONNECTION_EXPANDED_INFO}
      >
        <summary>Advanced</summary>
        <Callout className="launchpad-dashboard__connection-details__advanced__callout">
          {hasValidSsoProvider && (
            <div
              className={
                hasInternalEndpoint
                  ? 'launchpad-dashboard__connection-details__advanced__section--with-spacing'
                  : undefined
              }
            >
              <Tooltip
                content="The identity provider used to authenticate users for this endpoint"
                placement="top"
              >
                <div className="launchpad-dashboard__connection-details__advanced__section-header">
                  SSO Provider
                </div>
              </Tooltip>
              {ssoProvider.providerType && (
                <div>Type: {formatProviderType(ssoProvider.providerType)}</div>
              )}
              <div>Provider: {ssoProvider.providerName}</div>
              <div>
                Client ID: <Code>{ssoProvider.clientId}</Code>
              </div>
            </div>
          )}
          {hasInternalEndpoint && (
            <div>
              <Tooltip
                content="Internal network address used by backend services like Voicebox to reach your endpoint"
                placement="top"
              >
                <div className="launchpad-dashboard__connection-details__advanced__section-header">
                  Internal Address
                </div>
              </Tooltip>
              <div>
                <Code>{connection.internalEndpoint}</Code>
              </div>
            </div>
          )}
        </Callout>
      </details>
    );
  };

  return (
    <div className="launchpad-dashboard__connection-details">
      <div className="launchpad-dashboard__connection-details__header">
        <H3>{connection.name}</H3>
        {connection.useSSO && (
          <Tag
            intent={
              connection.ssoProvider?.isValid === false
                ? Intent.WARNING
                : Intent.PRIMARY
            }
            minimal
            large
            data-testid={LAUNCHPAD_CONNECTION_SSO_BADGE}
          >
            {connectionDetails.tags.sso}
          </Tag>
        )}
      </div>
      <div className="launchpad-dashboard__connection-details__content">
        <div className="launchpad-dashboard__connection-details__row">
          <strong>Endpoint:</strong>
          <Code>{connection.endpoint}</Code>
          <CopyToClipboardButton
            textToCopy={connection.endpoint}
            tooltip="Copy Endpoint URL"
            minimal
            testId={LAUNCHPAD_COPY_ENDPOINT_BUTTON}
          />
        </div>
        {renderExpandedInfo()}
      </div>
      {connection.token && isCopyConnectionTokenButtonEnabled() && (
        <div className="launchpad-dashboard__connection-details__token">
          <CopyToClipboardButton
            textToCopy={connection.token}
            tooltip="Copy authentication token for use with Stardog CLI or API clients"
            buttonText="Copy Token"
            testId={LAUNCHPAD_COPY_TOKEN_BUTTON}
          />
        </div>
      )}
      {renderAuthorizationError()}
      <hr className="launchpad-dashboard__connection-details__divider" />
    </div>
  );
};
